import { ApiError } from './types'
import { getDeviceId } from '../lib/deviceId'

/**
 * Thin fetch wrapper for the platform API. Hits Cloud Functions via
 * the Firebase Hosting rewrite (`/api/*`) in prod, the Functions
 * emulator (`http://localhost:5001/...`) in dev — the rewrite handles
 * both. Auth is a Firebase ID token attached as Bearer; resolved
 * lazily so unauthenticated calls (search, KB lookups) work without
 * forcing a sign-in.
 */

let getIdToken: (() => Promise<string | null>) | null = null

/**
 * Wire the ID-token resolver once during app init. Web's main.tsx
 * subscribes to Firebase Auth state and calls setAuthTokenProvider
 * with a function that returns the current user's ID token.
 */
export function setAuthTokenProvider(fn: () => Promise<string | null>): void {
  getIdToken = fn
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  /** Skip the auth header even if a token is available (for public endpoints). */
  anonymous?: boolean
  /**
   * Don't route this response through the global session handler.
   *
   * Exactly one thing needs it: the device-claim call itself. Its 403
   * `device_evicted` is the *diagnosis* being fetched, and letting it re-enter
   * the handler that triggered the fetch is how you build an infinite loop.
   */
  skipSessionHandler?: boolean
  /** INTERNAL. Set on the single retry a device-recovery performs, so a
   *  recovery that does not actually recover cannot loop. */
  deviceRetried?: boolean
}

/* ───────────────────── GLOBAL SESSION FAILURE HANDLING ─────────────────────
 *
 * There was NONE of this before. Every caller decided for itself what a 401
 * meant, which in practice meant nobody did: the desktop shell has no auth gate
 * at all, so a session that had been revoked server-side kept rendering
 * ALREADY-FETCHED prices and supplier contact details until the user happened
 * to navigate. That is CLAUDE.md invariant 3 leaking through the back door —
 * the data was fetched legitimately, and then the authorization that justified
 * it went away and nothing noticed.
 *
 * So the transport layer notices, once, for everybody:
 *   401                         → the token is no longer good. Session over.
 *   403 `device_evicted`        → this DEVICE was signed out from another one.
 *   403 `device_not_registered` → this device holds no slot on an account that
 *                                 is at its limit. The server refuses it on
 *                                 EVERY route, so the interstitial is no longer
 *                                 something the client can decline to render —
 *                                 it is the only way back to a working session.
 *
 * The handler is registered by the shell (see components/DeviceGate.tsx); until
 * it is, these are inert and behave exactly as before.
 */

export type SessionFailure =
  | { kind: 'unauthenticated' }
  | { kind: 'device_evicted'; message: string }
  | { kind: 'device_unregistered'; message: string }

type SessionHandler = (failure: SessionFailure) => void
let sessionHandler: SessionHandler | null = null

/**
 * ── THE ONE-SHOT DEVICE RECOVERY ──────────────────────────────────────────
 *
 * `device_not_registered` is the one session failure with a fix the client can
 * perform unattended: re-claim this device. It is now also reachable in an
 * ORDINARY situation, because the server binds a device row to the `auth_time`
 * of the session that claimed it — so the instant a user re-authenticates on a
 * device they have used for months, the row belongs to their previous session
 * until the claim lands. On an account at its limit that window is a 403.
 *
 * Registering a recovery makes that window invisible: claim once, retry once,
 * and only surface anything if the claim comes back "you really are at your
 * limit" (which raises the interstitial itself). Left unregistered — the mobile
 * shell before it wires this up, any test — behaviour is exactly what it was.
 */
export type DeviceRecovery = () => Promise<boolean>
let deviceRecovery: DeviceRecovery | null = null
export function setDeviceRecoveryHandler(fn: DeviceRecovery | null): void { deviceRecovery = fn }

export function setSessionFailureHandler(fn: SessionHandler | null): void {
  sessionHandler = fn
  // Installing a handler starts a fresh session-failure context, so the
  // coalescing window from a PREVIOUS handler cannot swallow the new one's
  // first report. (It is also what makes this testable without a test-only
  // export — the alternative was a global that leaks state between cases.)
  lastFailureAt = 0
}

/**
 * Coalesce: one revoked session usually surfaces as a burst of failures (a
 * screen mounts, five widgets fetch). The user must see ONE interstitial, not
 * five, and the handler must not be re-entered while it is signing out.
 */
let lastFailureAt = 0
function reportSessionFailure(failure: SessionFailure): void {
  if (!sessionHandler) return
  const now = Date.now()
  if (now - lastFailureAt < 2000) return
  lastFailureAt = now
  try { sessionHandler(failure) } catch { /* a UI handler must never break a fetch */ }
}

/** Called on a successful authed response so a later failure isn't suppressed
 *  by the coalescing window of an unrelated earlier one. */
function noteHealthySession(): void { lastFailureAt = 0 }

function codeOf(body: unknown): string | null {
  if (typeof body === 'object' && body && 'code' in body) {
    const code = (body as { code: unknown }).code
    if (typeof code === 'string') return code
  }
  return null
}

/**
 * Is this 401 actually about the TOKEN?
 *
 * Not every 401 means "your session died". Several routes answer 401 for
 * reasons that have nothing to do with authentication — the caller is
 * perfectly signed in, they just lack a numeric supplier id:
 *
 *   seller/index.ts:475       401 { error: 'No supplier id on your account' }
 *   integrations/index.ts:144 401 { error: 'No user id on token' }
 *   credit/index.ts:393       401 { ok: false, error: 'NotYourOrder' }
 *
 * Every self-registered (wizard) supplier hits the first one the moment they
 * tap "Add stone", because callerMerchantId returns null without a numeric
 * userId — a known inherited gap, see CLAUDE.md. Treating that as a dead
 * session raises the full-viewport session overlay, whose only control signs
 * them out; they sign back in, tap the same button, and hit the same wall.
 * An unescapable loop for a real user population.
 *
 * So this is an ALLOWLIST, not a denylist, and it fails toward NOT gating:
 * a missed session failure just means the user sees an ordinary error and the
 * next authed call catches it, whereas a spurious one traps them. Note that
 * requireAuth's genuine 401s carry no `code` at all (shared/auth.ts:170,177) —
 * which is exactly why matching on the absence of a code would be wrong here.
 */
function isTokenFailure(code: string | null, body: unknown): boolean {
  if (code === 'unauthenticated') return true // resolveOrgAuth (shared/auth.ts:390)
  const error = typeof (body as { error?: unknown })?.error === 'string'
    ? (body as { error: string }).error
    : ''
  return /missing authorization|invalid or expired id token/i.test(error)
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }
  let authed = false
  if (!opts.anonymous && getIdToken) {
    const token = await getIdToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
      authed = true
      // THE DEVICE HEADER. Sent only alongside a token: it identifies a browser
      // profile, and there is no reason to hand it to an endpoint that isn't
      // going to attribute it to an account anyway. `getDeviceId()` is a
      // synchronous localStorage read, minted once per profile (lib/deviceId.ts
      // explains why it is per-profile and not per-tab — race 17).
      headers['X-Diaex-Device'] = getDeviceId()
    }
  }

  const res = await fetch(`/api${path}`, {
    method: opts.method || 'GET',
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  })

  // Try JSON first; fall back to text so we don't lose error context
  // from non-JSON responses (HTML 502s from the rewrite layer, etc.).
  let body: unknown
  const text = await res.text()
  try { body = text ? JSON.parse(text) : null } catch { body = text }

  if (!res.ok) {
    // Only a request that actually CARRIED a token can tell us anything about
    // the session: a 401 from an anonymous call just means "sign in", which is
    // the normal state of a logged-out visitor and must not trigger a gate.
    if (authed && !opts.skipSessionHandler) {
      const code = codeOf(body)
      const serverMessage = typeof (body as { error?: unknown })?.error === 'string'
        ? (body as { error: string }).error
        : null
      if (code === 'device_evicted') {
        reportSessionFailure({
          kind: 'device_evicted',
          message: serverMessage ?? 'This device was signed out.',
        })
      } else if (code === 'device_not_registered') {
        // Try to FIX it before telling anyone about it. One attempt, guarded by
        // `deviceRetried` so a recovery that keeps failing cannot loop.
        if (deviceRecovery && !opts.deviceRetried) {
          let recovered = false
          try { recovered = await deviceRecovery() } catch { recovered = false }
          if (recovered) return await api<T>(path, { ...opts, deviceRetried: true })
          // Not recovered ⇒ the claim itself has already raised the eviction
          // interstitial (409) or decided the whole thing is inert. Reporting
          // here as well would only re-enter the same claim.
        } else {
          reportSessionFailure({
            kind: 'device_unregistered',
            message: serverMessage ?? 'You’re signed in on too many devices.',
          })
        }
      } else if (res.status === 401 && isTokenFailure(code, body)) {
        reportSessionFailure({ kind: 'unauthenticated' })
      }
    }
    const message =
      (typeof body === 'object' && body && 'error' in body && typeof (body as { error: unknown }).error === 'string')
        ? (body as { error: string }).error
        : `HTTP ${res.status}`
    throw new ApiError(res.status, message, body)
  }
  if (authed) noteHealthySession()
  return body as T
}
